import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { type UUID } from '~/types/common';

import { Icon } from '@vestigas/ui';

import { LedgerAccountForm } from './LedgerAccountForm';

type P = {
  readonly isOpen: boolean;
  readonly ledgerAccountId?: UUID;
  readonly onClose: () => void;
};

/**
 * Thin dialog shell around `LedgerAccountForm`.
 * The form owns its own action, so this wrapper renders no `DialogActions`
 * — that is what keeps the form renderable as a standalone page outside a dialog.
 */
export function LedgerAccountFormDialog({
  isOpen,
  ledgerAccountId,
  onClose,
}: P) {
  const { t } = useTranslation('ledgerAccounts');

  const isEditMode = Boolean(ledgerAccountId);

  return (
    <Dialog
      // The form's Select/MultiSelect fields handle Escape themselves to
      // close their own dropdown, but let the key bubble on -- without this,
      // closing their dropdown also discards the whole form.
      disableEscapeKeyDown
      fullWidth
      maxWidth="md"
      open={isOpen}
      onClose={onClose}
    >
      <DialogTitle className="flex items-center justify-between">
        {isEditMode ? t(($) => $.form.titleEdit) : t(($) => $.form.titleCreate)}
        <IconButton aria-label="close" size="small" onClick={onClose}>
          <Icon metaphor="close" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Keyed on the id so switching between edit targets (or from edit
            back to create) without closing the dialog remounts the form
            with a fresh state instead of reusing stale field values. */}
        <LedgerAccountForm
          key={ledgerAccountId ?? 'new'}
          ledgerAccountId={ledgerAccountId}
          onCancel={onClose}
          onSuccess={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}
